import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip,
} from 'recharts';
import { Card, CardContent } from '../components/ui/card';
import { Accessibility, Target, TrendingUp, CheckCircle2, Clock, AlertOctagon, FileText, ShieldCheck, Printer, Download } from 'lucide-react';

const STATUS_META = {
  pending: { l: 'Pendente', c: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Clock },
  in_progress: { l: 'Em Andamento', c: 'bg-blue-100 text-blue-800 border-blue-200', Icon: TrendingUp },
  completed: { l: 'Concluída', c: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  blocked: { l: 'Bloqueada', c: 'bg-red-100 text-red-800 border-red-200', Icon: AlertOctagon },
};
const PRIORITY_META = {
  high: { l: 'Alta', c: 'bg-red-100 text-red-800' },
  medium: { l: 'Média', c: 'bg-amber-100 text-amber-800' },
  low: { l: 'Baixa', c: 'bg-blue-100 text-blue-800' },
};

const KpiCard = ({ label, value, sub, icon: Icon, color = 'teal' }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 text-${color}-700 flex items-center justify-center`}><Icon size={20} /></div>
      </div>
    </CardContent>
  </Card>
);

const ClientReportPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/client/reports/${token}`);
      setData(data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Link inválido ou revogado' : 'Erro ao carregar');
    } finally { setLoading(false); }
  }, [API_URL, token]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API_URL}/api/client/reports/${token}/pdf`, {
        responseType: 'blob',
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diagnostico-inclusao-${data?.company?.trade_name || 'relatorio'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Não foi possível gerar o PDF. Tente novamente.');
    } finally { setDownloadingPdf(false); }
  };

  const radarData = useMemo(() => {
    if (!data?.latest_assessment) return [];
    return data.latest_assessment.dimensions.map((d, i) => ({
      dimension: `${i + 1}. ${d.label.split(' ').slice(0, 2).join(' ')}`,
      fullLabel: d.label,
      score: d.score,
      fullMark: 4,
    }));
  }, [data]);

  const progressPct = (a) => {
    if (!a.target_value) return 0;
    return Math.min(100, Math.max(0, ((a.current_value || 0) / a.target_value) * 100));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <ShieldCheck size={48} className="text-gray-300 mb-4" />
        <h1 className="text-xl font-bold text-gray-900">{error || 'Erro'}</h1>
        <p className="text-sm text-gray-600 mt-2 text-center max-w-md">
          Entre em contato com a Chico da Inclusão para obter um link atualizado.
        </p>
      </div>
    );
  }

  const c = data.company;
  const a = data.latest_assessment;
  const s = data.actions_summary;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 print:static">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 text-white flex items-center justify-center">
              <Accessibility size={22} />
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Chico da Inclusão</div>
              <div className="text-base font-bold text-gray-900">Portal do Cliente</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-sm font-semibold text-white print:hidden"
              data-testid="client-download-pdf-button"
            >
              <Download size={14} /> {downloadingPdf ? 'Gerando...' : 'Baixar PDF'}
            </button>
            <button
              onClick={() => window.print()}
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 print:hidden"
              data-testid="client-print-button"
            >
              <Printer size={14} /> Imprimir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="client-report-content">
        {/* Company hero */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-semibold mb-3">
            <ShieldCheck size={12} /> Diagnóstico de Inclusão
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{c.trade_name}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {[c.industry, [c.city, c.state].filter(Boolean).join('/')].filter(Boolean).join(' · ')}
          </p>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="client-kpis">
          <KpiCard
            label="Score de Maturidade"
            value={a ? a.overall_score.toFixed(2) : '—'}
            sub={a?.maturity_stage || 'Sem diagnóstico'}
            icon={Target}
            color="teal"
          />
          <KpiCard
            label="Ações no Plano"
            value={s.total}
            sub={`${s.completion_rate}% concluído`}
            icon={FileText}
            color="blue"
          />
          <KpiCard
            label="Em Andamento"
            value={s.by_status.in_progress}
            sub={`${s.by_status.completed} concluídas`}
            icon={TrendingUp}
            color="amber"
          />
          <KpiCard
            label="Concluídas"
            value={s.by_status.completed}
            sub={`${s.by_status.blocked} bloqueadas`}
            icon={CheckCircle2}
            color="emerald"
          />
        </div>

        {/* Radar + Dimensions */}
        {a && radarData.length > 0 && (
          <Card className="mb-8" data-testid="client-radar">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Mapa de Maturidade</h2>
              <p className="text-xs text-gray-500 mb-4">Pontuação atual em cada uma das 12 dimensões de inclusão</p>
              <ResponsiveContainer width="100%" height={380}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11, fill: '#374151' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fontSize: 10 }} />
                  <Radar name="Score" dataKey="score" stroke="#0d9488" fill="#0d9488" fillOpacity={0.4} strokeWidth={2} />
                  <Tooltip formatter={(v) => [`${v}/4`, 'Score']} labelFormatter={(l, p) => p?.[0]?.payload?.fullLabel || l} contentStyle={{ borderRadius: 8 }} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {data.actions.length > 0 && (
          <Card className="mb-8" data-testid="client-actions">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Plano de Ação Atual</h2>
              <p className="text-xs text-gray-500 mb-4">{s.total} ações · {s.completion_rate}% concluídas</p>
              <div className="space-y-3">
                {data.actions.map((act) => {
                  const st = STATUS_META[act.status] || STATUS_META.pending;
                  const pr = PRIORITY_META[act.priority] || PRIORITY_META.medium;
                  const StIcon = st.Icon;
                  const pct = progressPct(act);
                  return (
                    <div key={act.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-teal-700 font-bold uppercase tracking-wider mb-0.5">{act.dimension_label}</div>
                          <div className="font-semibold text-gray-900">{act.title}</div>
                          {act.description && <div className="text-sm text-gray-600 mt-1">{act.description}</div>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold border ${st.c}`}>
                            <StIcon size={11} /> {st.l}
                          </span>
                          <span className={`inline-block px-2 py-1 rounded-full text-[11px] font-bold ${pr.c}`}>{pr.l}</span>
                        </div>
                      </div>
                      {act.kpi_label && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span className="font-medium">{act.kpi_label}</span>
                            <span className="font-mono">{act.current_value || 0} / {act.target_value || 0} {act.kpi_unit || ''}</span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )}
                      {(act.responsible || act.due_date) && (
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-2">
                          {act.responsible && <span>Responsável: <strong className="text-gray-700">{act.responsible}</strong></span>}
                          {act.due_date && <span>Prazo: <strong className="text-gray-700">{act.due_date}</strong></span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Full report */}
        {a?.generated_report && (
          <Card data-testid="client-report">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Relatório Diagnóstico Completo</h2>
              <p className="text-xs text-gray-500 mb-4">
                Gerado em {a.report_generated_at ? new Date(a.report_generated_at).toLocaleDateString('pt-BR') : '-'}
              </p>
              <div className="prose prose-sm md:prose-base max-w-none prose-headings:text-gray-900 prose-a:text-teal-600 prose-strong:text-gray-900 prose-table:text-xs">
                <ReactMarkdown>{a.generated_report}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-500">
          <p>Portal seguro · Acesso via link único · <strong>Chico da Inclusão</strong></p>
        </div>
      </main>
    </div>
  );
};

export default ClientReportPage;
